'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  InputBase,
  Button,
  Stack,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SaveIcon from '@mui/icons-material/Save';
import { lusitana } from '@/components/app/proyectos/raceway/fonts';
import {
  fetchTimeSettings,
  saveTimeSettings,
  executePhase,
  cancelPhase,
} from '@/lib/proyectos/raceway/raceway';
import { TimeSettings, TankSnapshot } from '@/types/proyectos/raceway';

// ─── Tipos ───────────────────────────────────────────────────────────────────

type PhaseName = 'llenado' | 'vaciado' | 'gases_cultivo' | 'volcar_raceway';
type DepositoKey = 'dep_raceway' | 'dep_cultivo' | 'dep_cosecha';

interface Props {
  /** Estado live de los tanques (opcional, para mostrar fase activa) */
  tanks?: {
    dep_raceway?: TankSnapshot;
    dep_cultivo?: TankSnapshot;
    dep_cosecha?: TankSnapshot;
  };
}

// ─── Config depósitos / fases ─────────────────────────────────────────────────

const DEPOSITOS = [
  {
    key: 'dep_raceway' as DepositoKey,
    title: 'Dep. Raceway',
    color: '#1d4ed8',
    phases: [
      { id: 'llenado',  label: 'Llenado'  },
      { id: 'vaciado',  label: 'Vaciado'  },
    ] as { id: string; label: string }[],
  },
  {
    key: 'dep_cultivo' as DepositoKey,
    title: 'Dep. Cultivo',
    color: '#0891b2',
    phases: [
      { id: 'llenado',        label: 'Llenado'           },
      { id: 'vaciado',        label: 'Vaciado'           },
      { id: 'gases_cultivo',  label: 'Gases O₂+CO₂'     },
      { id: 'volcar_raceway', label: 'Volcar → Raceway'  },
    ] as { id: string; label: string }[],
  },
  {
    key: 'dep_cosecha' as DepositoKey,
    title: 'Dep. Cosecha',
    color: '#0369a1',
    phases: [
      { id: 'llenado', label: 'Llenado' },
    ] as { id: string; label: string }[],
  },
] as const;

const PHASE_LABELS: Record<string, string> = {
  llenado: 'Llenado',
  vaciado: 'Vaciado',
  gases_cultivo: 'Gases O₂+CO₂',
  volcar_raceway: 'Volcar → Raceway',
};

const DEFAULT_SETTINGS: TimeSettings = {
  dep_raceway: { llenado: 60, vaciado: 60 },
  dep_cultivo: { llenado: 60, vaciado: 60, gases_cultivo: 60, volcar_raceway: 60 },
  dep_cosecha: { llenado: 60 },
};

// ─── Componente ───────────────────────────────────────────────────────────────

export default function TimeSettingsPanel({ tanks }: Props) {
  const [settings, setSettings] = useState<TimeSettings>(DEFAULT_SETTINGS);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  // { 'dep_raceway:llenado': true } → fase ejecutándose localmente
  const [running, setRunning] = useState<Record<string, boolean>>({});

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchTimeSettings()
      .then(setSettings)
      .catch(() => setError('No se pudieron cargar los tiempos'))
      .finally(() => setLoading(false));
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getVal = (dep: DepositoKey, phase: string): number =>
    ((settings[dep] as Record<string, number>)[phase]) ?? 0;

  const setVal = (dep: DepositoKey, phase: string, val: number) => {
    const clamped = Math.min(120, Math.max(0, val));
    setSettings(prev => ({
      ...prev,
      [dep]: { ...(prev[dep] as object), [phase]: clamped },
    }));
  };

  // ── Guardar tiempos ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveTimeSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Ejecutar / cancelar fase ──────────────────────────────────────────────
  const handleExecute = useCallback(async (dep: DepositoKey, phase: string) => {
    const key = `${dep}:${phase}`;
    setRunning(prev => ({ ...prev, [key]: true }));
    setError(null);
    try {
      await executePhase(dep, phase);
    } catch (e: any) {
      setError(e.message ?? 'Error al ejecutar fase');
    } finally {
      setRunning(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const handleCancel = useCallback(async (dep: DepositoKey) => {
    const key = `${dep}:cancel`;
    setRunning(prev => ({ ...prev, [key]: true }));
    setError(null);
    try {
      await cancelPhase(dep);
    } catch (e: any) {
      setError(e.message ?? 'Error al cancelar');
    } finally {
      setRunning(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // ── Skeleton ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid #e5e7eb', bgcolor: '#f9fafb' }}>
        <Stack spacing={2}>
          {[1, 2, 3].map((_, i) => (
            <Box key={i}>
              <Skeleton width={150} height={24} sx={{ mb: 1 }} />
              <Stack spacing={1}>
                {[1, 2].map((_, j) => <Skeleton key={j} variant="rounded" height={44} />)}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: '16px', border: '1px solid #e5e7eb', bgcolor: '#f9fafb' }}
    >
      {/* Cabecera */}
      <Box sx={{ mb: 2 }}>
        <Typography
          className={lusitana.className}
          sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1d4ed8' }}
        >
          Control de Fases y Tiempos
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          Configura la duración (segundos) y ejecuta cada fase
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Depósitos */}
      <Stack spacing={3}>
        {DEPOSITOS.map((dep) => {
          const tank      = tanks?.[dep.key];
          const activePhase = tank?.current_phase ?? null;
          const busy        = tank?.busy ?? false;
          const cancelKey   = `${dep.key}:cancel`;

          return (
            <Box key={dep.key}>
              {/* Título depósito + estado */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 10, height: 10, borderRadius: '50%',
                    bgcolor: busy ? '#22c55e' : '#d1d5db',
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: dep.color, flex: 1 }}>
                  {dep.title}
                </Typography>
                {activePhase && (
                  <Chip
                    label={PHASE_LABELS[activePhase] ?? activePhase}
                    size="small"
                    sx={{
                      height: 20, fontSize: '0.65rem', fontWeight: 600,
                      bgcolor: `${dep.color}18`, color: dep.color,
                      border: `1px solid ${dep.color}40`,
                    }}
                  />
                )}
                {busy && (
                  <Tooltip title="Cancelar fase activa">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={
                        running[cancelKey]
                          ? <CircularProgress size={10} color="inherit" />
                          : <StopIcon sx={{ fontSize: 14 }} />
                      }
                      disabled={running[cancelKey]}
                      onClick={() => handleCancel(dep.key)}
                      sx={{
                        py: 0, px: 1, minWidth: 0, fontSize: '0.65rem',
                        borderRadius: '8px', textTransform: 'none',
                      }}
                    >
                      Cancelar
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {/* Fases */}
              <Stack spacing={1}>
                {dep.phases.map((ph) => {
                  const runKey   = `${dep.key}:${ph.id}`;
                  const isActive = activePhase === ph.id;
                  const isRunning = running[runKey];

                  return (
                    <Box
                      key={ph.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: isActive ? `${dep.color}12` : 'white',
                        borderRadius: '12px',
                        border: `1px solid ${isActive ? dep.color + '50' : '#e5e7eb'}`,
                        px: 1.5,
                        py: 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      {/* Label */}
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? dep.color : '#4b5563',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {ph.label}
                      </Typography>

                      {/* Input duración */}
                      <InputBase
                        type="number"
                        value={getVal(dep.key, ph.id)}
                        onChange={e => setVal(dep.key, ph.id, Number(e.target.value))}
                        inputProps={{ min: 0, max: 120, step: 5 }}
                        sx={{
                          width: 64,
                          height: 30,
                          bgcolor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: '#1f2937',
                          px: 1,
                          '& input': { textAlign: 'center', p: 0 },
                          '&:focus-within': {
                            borderColor: dep.color,
                            boxShadow: `0 0 0 2px ${dep.color}30`,
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af', minWidth: 18 }}>
                        seg
                      </Typography>

                      {/* Botón ejecutar */}
                      <Tooltip title={isActive ? 'Fase activa' : `Ejecutar: ${ph.label}`}>
                        <span>
                          <Button
                            size="small"
                            variant={isActive ? 'contained' : 'outlined'}
                            disabled={busy || isRunning}
                            onClick={() => handleExecute(dep.key, ph.id)}
                            sx={{
                              minWidth: 0,
                              width: 30,
                              height: 30,
                              p: 0,
                              borderRadius: '8px',
                              borderColor: dep.color,
                              bgcolor: isActive ? dep.color : 'transparent',
                              color: isActive ? 'white' : dep.color,
                              '&:hover': {
                                bgcolor: isActive ? dep.color : `${dep.color}18`,
                              },
                              '&.Mui-disabled': {
                                opacity: 0.4,
                              },
                            }}
                          >
                            {isRunning
                              ? <CircularProgress size={12} color="inherit" />
                              : isActive
                              ? <StopIcon sx={{ fontSize: 14 }} />
                              : <PlayArrowIcon sx={{ fontSize: 14 }} />
                            }
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      {/* Botón guardar tiempos */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={
            saving
              ? <CircularProgress size={14} color="inherit" />
              : <SaveIcon sx={{ fontSize: 16 }} />
          }
          sx={{
            minWidth: 160,
            borderRadius: '20px',
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            boxShadow: 'none',
            py: 0.8,
            bgcolor: saved ? '#10b981' : '#3b82f6',
            '&:hover': {
              bgcolor: saved ? '#059669' : '#2563eb',
              boxShadow: 'none',
            },
          }}
        >
          {saving ? 'Guardando…' : saved ? '✓ Guardado' : 'Guardar Tiempos'}
        </Button>
      </Box>
    </Paper>
  );
}